import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShippingFast, 
  faGift, 
  faLeaf, 
  faPercent,
  faChevronDown,
  faArrowRight,
  faStar,
  faQuoteLeft,
  faChevronLeft,
  faChevronRight,
  faCircle,
  faPalette,
  faVial
} from '@fortawesome/free-solid-svg-icons';
import { theme } from '../styles/theme';
import { Button, Grid, Badge, Section, Container } from '../styles/components';
import ProductCard from '../components/product/ProductCard';
import { mockProducts, mockCategories } from '../utils/mockData';
import heroBackgroundImg from '../assets/images/hero-bg-new.jpg';
import showcaseImage from '../assets/images/showcase-image.jpg';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const shine = keyframes`
  0% {
    left: -100%;
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    left: 100%;
    opacity: 0;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Background images for home hero section
const homeBackgroundImages = [
  'https://images.unsplash.com/photo-1600612253971-422e7f7faeb6?ixlib=rb-4.0.3&auto=format&fit=crop&q=85',
  'https://images.unsplash.com/photo-1615368144592-35d89df9b19e?ixlib=rb-4.0.3&auto=format&fit=crop&q=85',
  'https://images.unsplash.com/photo-1595425964143-5a3815a6be70?ixlib=rb-4.0.3&auto=format&fit=crop&q=85',
  'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?ixlib=rb-4.0.3&auto=format&fit=crop&q=85',
  'https://images.unsplash.com/photo-1607344645866-009c320b63e0?ixlib=rb-4.0.3&auto=format&fit=crop&q=85',
  'https://images.unsplash.com/photo-1608248597279-f99d160bfcbc?ixlib=rb-4.0.3&auto=format&fit=crop&q=85'
];

// Helper function to get a random image
const getRandomHomeBackground = (): string => {
  return homeBackgroundImages[Math.floor(Math.random() * homeBackgroundImages.length)];
};

// Using the same hero background image as other pages
const heroBackgroundImage = heroBackgroundImg;

const HeroSection = styled.section`
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.background};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg, 
      rgba(0, 0, 0, 0.7) 0%, 
      rgba(0, 0, 0, 0.5) 40%, 
      rgba(0, 0, 0, 0.4) 100%
    ),
    url('${heroBackgroundImage}');
    background-size: cover;
    background-position: center;
    z-index: -1;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 150px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.95), transparent);
  z-index: 0;
`;

const GlassPanel = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 85%;
  height: 75%;
  background: rgba(255, 255, 255, 0.03);
  border-radius: ${theme.borderRadius.medium};
  backdrop-filter: blur(4px);
  z-index: 0;
  border: 1px solid rgba(191, 149, 63, 0.08);
  box-shadow: 0 0 80px rgba(0, 0, 0, 0.3);
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 100px;
    height: 100px;
    border-radius: 50%;
  }
  
  &::before {
    top: -30px;
    right: 20%;
    background: radial-gradient(circle at center, rgba(191, 149, 63, 0.4) 0%, rgba(191, 149, 63, 0) 70%);
    filter: blur(20px);
  }
  
  &::after {
    bottom: -40px;
    left: 15%;
    background: radial-gradient(circle at center, rgba(191, 149, 63, 0.3) 0%, rgba(191, 149, 63, 0) 70%);
    filter: blur(25px);
  }
`;

const BackgroundDecoration = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiPgogIDxkZWZzPgogICAgPHBhdHRlcm4gaWQ9InBhdHRlcm4iIHg9IjAiIHk9IjAiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgcGF0dGVyblRyYW5zZm9ybT0icm90YXRlKDQ1KSI+CiAgICAgIDxjaXJjbGUgY3g9IjIiIGN5PSIyIiByPSIxIiBmaWxsPSIjQkY5NTNGIiBmaWxsLW9wYWNpdHk9IjAuMSIvPgogICAgPC9wYXR0ZXJuPgogIDwvZGVmcz4KICA8cmVjdCB4PSIwIiB5PSIwIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI3BhdHRlcm4pIiAvPgo8L3N2Zz4=');
  opacity: 0.3;
  z-index: -1;
`;

const HeroDecoration = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  border: 1px solid rgba(191, 149, 63, 0.15);
  border-radius: 50%;
  top: calc(50% - 150px);
  left: calc(50% - 150px);
  z-index: 0;
  opacity: 0.5;
  
  &::after {
    content: '';
    position: absolute;
    width: 450px;
    height: 450px;
    border: 1px dashed rgba(166, 124, 0, 0.12);
    border-radius: 50%;
    top: -75px;
    left: -75px;
  }
`;

const LightBeam = styled.div`
  position: absolute;
  top: -100px;
  left: 20%;
  width: 60%;
  height: 250px;
  background: linear-gradient(to bottom, rgba(191, 149, 63, 0.4), rgba(191, 149, 63, 0));
  transform: rotate(-45deg);
  filter: blur(60px);
  opacity: 0.5;
  z-index: 0;
`;

const StarIcon = styled.div`
  position: absolute;
  color: ${theme.colors.primary};
  opacity: 0.6;
  font-size: 0.8rem;
  animation: twinkle 3s ease-in-out infinite;
  
  @keyframes twinkle {
    0%, 100% {
      opacity: 0.2;
    }
    50% {
      opacity: 0.7;
    }
  }
  
  &:nth-child(1) {
    top: 15%;
    left: 10%;
    animation-delay: 0.5s;
  }
  &:nth-child(2) {
    top: 25%;
    right: 15%;
    animation-delay: 1.5s;
  }
  &:nth-child(3) {
    bottom: 20%;
    right: 10%;
    animation-delay: 1s;
  }
  &:nth-child(4) {
    bottom: 30%;
    left: 5%;
    animation-delay: 2s;
  }
  &:nth-child(5) {
    top: 50%;
    right: 5%;
    animation-delay: 0.7s;
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  padding: ${theme.spacing.xl} ${theme.spacing.lg};
  text-align: center;
  animation: ${fadeIn} 1s ease-out;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: ${theme.spacing.lg};
  font-family: ${theme.fonts.heading};
  letter-spacing: 0.03em;
  line-height: 1.2;
  text-shadow: 0 2px 15px rgba(0, 0, 0, 0.5);
  animation: ${fadeInUp} 0.8s ease-out forwards;
  opacity: 0;
  animation-delay: 0.2s;
  
  span {
    display: block;
    color: ${theme.colors.primary};
    position: relative;
    margin-top: ${theme.spacing.sm};
    font-weight: 700;
    
    &::after {
      content: '';
      position: absolute;
      width: 60%;
      height: 4px;
      bottom: -10px;
      left: 20%;
      background: ${theme.colors.gradient.gold};
      border-radius: ${theme.borderRadius.pill};
      box-shadow: 0 0 20px rgba(191, 149, 63, 0.5);
    }
  }
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    font-size: 4.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  margin-bottom: ${theme.spacing.xl};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.8;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.4);
  animation: ${fadeInUp} 0.8s ease-out forwards;
  opacity: 0;
  animation-delay: 0.4s;
  letter-spacing: 0.02em;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    font-size: 1.5rem;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: center;
  margin-bottom: ${theme.spacing.xl};
  animation: ${fadeInUp} 0.8s ease-out forwards;
  opacity: 0;
  animation-delay: 0.6s;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
    width: 100%;
  }
`;

const PrimaryButton = styled(Button)`
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  font-size: 1.1rem;
  font-weight: 600;
  min-width: 180px;
  border-radius: ${theme.borderRadius.pill};
  box-shadow: ${theme.shadows.large}, ${theme.shadows.glow};
  position: relative;
  overflow: hidden;
  background: ${theme.colors.gradient.gold};
  border: none;
  color: ${theme.colors.secondary};
  letter-spacing: 0.05em;
  transform: translateY(0);
  transition: all 0.3s ease;
  text-transform: uppercase;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.5) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: ${shine} 3s infinite;
    animation-delay: 1s;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${theme.shadows.xl}, ${theme.shadows.gold};
  }
`;

const SecondaryButton = styled(Button)`
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  font-size: 1.1rem;
  min-width: 180px;
  background: transparent;
  color: ${theme.colors.background};
  border: 2px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.pill};
  letter-spacing: 0.05em;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    background: rgba(191, 149, 63, 0.1);
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    border-color: ${theme.colors.primaryLight};
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    border: 1px solid ${theme.colors.primary};
    opacity: 0.3;
    border-radius: ${theme.borderRadius.pill};
    pointer-events: none;
    transition: all 0.3s ease;
  }
  
  &:hover::after {
    top: -6px;
    left: -6px;
    right: -6px;
    bottom: -6px;
    opacity: 0.1;
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: ${theme.spacing.xl};
  left: 50%;
  transform: translateX(-50%);
  animation: bounce 2s infinite;
  cursor: pointer;
  color: ${theme.colors.primary};
  font-size: 2rem;
  opacity: 0.8;
  z-index: 5;
  
  &:hover {
    opacity: 1;
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {transform: translateY(0) translateX(-50%);}
    40% {transform: translateY(-10px) translateX(-50%);}
    60% {transform: translateY(-5px) translateX(-50%);}
  }
`;

const BannerSection = styled.section`
  background: ${theme.colors.gradient.gold};
  padding: ${theme.spacing.lg} 0;
  text-align: center;
  color: ${theme.colors.secondary};
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: ${shimmer} 5s infinite linear;
    background-size: 200% 100%;
  }
`;

const BannerTitle = styled.h3`
  font-size: 1.5rem;
  margin: 0;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${theme.spacing.sm};
  }
`;

const BannerDivider = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${theme.colors.secondary};
  margin: 0 ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: ${theme.spacing.lg};
  font-family: ${theme.fonts.heading};
  position: relative;
  
  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background: ${theme.colors.gradient.gold};
    margin: ${theme.spacing.sm} auto 0;
    border-radius: ${theme.borderRadius.small};
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  max-width: 700px;
  margin: 0 auto ${theme.spacing.xl};
  color: ${theme.colors.textLight};
  font-size: 1.1rem;
  line-height: 1.6;
`;

const CategoryCard = styled(Link)`
  position: relative;
  border-radius: ${theme.borderRadius.large};
  overflow: hidden;
  height: 300px;
  display: block;
  color: ${theme.colors.background};
  text-decoration: none;
  box-shadow: ${theme.shadows.medium};
  transition: ${theme.transitions.medium};
  
  &:hover {
    transform: translateY(-5px) scale(1.02);
    box-shadow: ${theme.shadows.large}, ${theme.shadows.glow};
    
    img {
      transform: scale(1.1);
    }
    
    .category-overlay {
      background: linear-gradient(to top, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.4));
    }
    
    .category-button {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.8s ease;
  }
`;

const CategoryOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.3));
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: ${theme.spacing.lg};
  transition: background 0.3s ease;
  class-name: category-overlay;
`;

const CategoryName = styled.h3`
  font-size: 1.8rem;
  margin-bottom: ${theme.spacing.sm};
`;

const CategoryDescription = styled.p`
  font-size: 1rem;
  opacity: 0.9;
`;

const CategoryButton = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
  color: ${theme.colors.primary};
  font-weight: 500;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
  class-name: category-button;
`;

const FeatureSection = styled(Section)`
  background-color: ${theme.colors.backgroundAlt};
  position: relative;
  overflow: hidden;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled.div`
  text-align: center;
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.background};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.small};
  transition: ${theme.transitions.medium};
  border: 1px solid rgba(0, 0, 0, 0.05);
  height: 100%;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${theme.shadows.medium};
    border-color: rgba(255, 215, 0, 0.3);
    
    svg {
      color: ${theme.colors.primary};
      transform: scale(1.1);
    }
  }
  
  svg {
    font-size: 2.5rem;
    color: ${theme.colors.secondary};
    margin-bottom: ${theme.spacing.md};
    transition: ${theme.transitions.medium};
  }
  
  h3 {
    margin-bottom: ${theme.spacing.sm};
    font-size: 1.25rem;
  }
  
  p {
    color: ${theme.colors.textLight};
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const ProductSection = styled(Section)`
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    right: -150px;
    top: -150px;
    width: 300px;
    height: 300px;
    background: ${theme.colors.primaryLight};
    opacity: 0.3;
    border-radius: 50%;
    z-index: -1;
  }
  
  &:after {
    content: '';
    position: absolute;
    left: -100px;
    bottom: -100px;
    width: 200px;
    height: 200px;
    background: ${theme.colors.primaryLight};
    opacity: 0.2;
    border-radius: 50%;
    z-index: -1;
  }
`;

const ViewAllButton = styled(Button)`
  position: relative;
  overflow: hidden;
`;

const FeaturedBadge = styled(Badge)`
  position: absolute;
  top: ${theme.spacing.md};
  left: ${theme.spacing.md};
  z-index: 5;
`;

// New Testimonial Components
const TestimonialSection = styled(Section)`
  background-color: ${theme.colors.backgroundAlt};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(212, 175, 55, 0.1) 0%, rgba(212, 175, 55, 0) 70%);
    z-index: 0;
  }
`;

const TestimonialCarousel = styled.div`
  position: relative;
  max-width: 900px;
  margin: 0 auto;
  padding: ${theme.spacing.xl} 0;
`;

const TestimonialItem = styled.div<{active?: boolean}>`
  text-align: center;
  padding: ${theme.spacing.xl};
  background: ${theme.colors.background};
  border-radius: ${theme.borderRadius.large};
  box-shadow: ${theme.shadows.medium};
  margin: ${theme.spacing.lg} auto;
  max-width: 800px;
  position: relative;
  opacity: ${props => props.active ? 1 : 0};
  display: ${props => props.active ? 'block' : 'none'};
  transition: opacity 0.5s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border-radius: ${theme.borderRadius.large};
    background: ${theme.colors.gradient.gold};
    z-index: -1;
    opacity: 0.3;
  }
`;

const QuoteIcon = styled.div`
  color: ${theme.colors.primary};
  font-size: 2rem;
  margin-bottom: ${theme.spacing.md};
  opacity: 0.8;
`;

const TestimonialText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  font-style: italic;
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text};
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${theme.spacing.lg};
`;

const AuthorImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${theme.colors.primary};
  margin-right: ${theme.spacing.md};
`;

const AuthorInfo = styled.div`
  text-align: left;
`;

const AuthorName = styled.h4`
  margin: 0 0 ${theme.spacing.xs};
  font-weight: 600;
`;

const AuthorTitle = styled.p`
  margin: 0;
  color: ${theme.colors.textLight};
  font-size: 0.9rem;
`;

const CarouselControls = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};
`;

const CarouselButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.textLight};
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  
  &:hover {
    color: ${theme.colors.primary};
    background: rgba(212, 175, 55, 0.1);
  }
`;

const CarouselIndicators = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
`;

const CarouselIndicator = styled.span<{active?: boolean}>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.active ? theme.colors.primary : theme.colors.gray};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? theme.colors.primary : theme.colors.darkGray};
  }
`;

// New Trending Products Components
const TrendingSection = styled(Section)`
  background: linear-gradient(to right, ${theme.colors.background} 0%, ${theme.colors.backgroundAlt} 100%);
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: 0;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(212, 175, 55, 0.1) 0%, rgba(212, 175, 55, 0) 70%);
    z-index: 0;
  }
`;

const ProductCarousel = styled.div`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: ${theme.spacing.md} 0;
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ProductCarouselItem = styled.div`
  flex: 0 0 auto;
  width: 300px;
  margin-right: ${theme.spacing.md};
  
  &:last-child {
    margin-right: 0;
  }
`;

const CarouselNavigation = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`;

const NavButton = styled.button`
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.gray};
  color: ${theme.colors.text};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.primary};
    color: ${theme.colors.background};
    border-color: ${theme.colors.primary};
  }
`;

// New Showcase Item
const ShowcaseSection = styled(Section)`
  padding: ${theme.spacing.xxl} 0;
`;

const ShowcaseGrid = styled.div`
  display: grid;
  grid-template-columns: 6fr 4fr;
  gap: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const ShowcaseImage = styled.div`
  position: relative;
  height: 500px;
  overflow: hidden;
  border-radius: ${theme.borderRadius.large};
  box-shadow: ${theme.shadows.large};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(0deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 50%);
    pointer-events: none;
  }
`;

const ShowcaseContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${theme.spacing.lg};
`;

const ShowcaseTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: ${theme.spacing.md};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 60px;
    height: 3px;
    background: ${theme.colors.primary};
    bottom: -${theme.spacing.sm};
    left: 0;
  }
`;

const ShowcaseDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${theme.colors.textLight};
  margin-bottom: ${theme.spacing.lg};
`;

const FeatureListItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  
  svg {
    color: ${theme.colors.primary};
    margin-right: ${theme.spacing.md};
  }
  
  p {
    margin: 0;
    font-weight: 500;
  }
`;

// Animation helpers
const FadeInElement = styled.div<{delay?: string}>`
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
`;

// Floating Object for decoration
const FloatingObject = styled.div`
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: radial-gradient(circle at center, rgba(191, 149, 63, 0.2) 0%, rgba(191, 149, 63, 0) 70%);
  animation: ${float} 6s ease-in-out infinite;
  z-index: 0;
  
  &:nth-child(1) {
    top: 20%;
    left: 10%;
    width: 120px;
    height: 120px;
    animation-duration: 7s;
  }
  
  &:nth-child(2) {
    bottom: 15%;
    right: 5%;
    width: 80px;
    height: 80px;
    animation-duration: 5s;
  }
`;

const HomePage: React.FC = () => {
  // Get featured products
  const featuredProducts = mockProducts
    .filter(product => product.featured)
    .slice(0, 4);
  
  // Get trending products (highest rated)
  const trendingProducts = [...mockProducts]
    .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0))
    .slice(0, 6);
  
  const heroRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const productCarouselRef = useRef<HTMLDivElement>(null);
  
  // Testimonial state
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  
  // Mock testimonials
  const testimonials = [
    {
      id: 1,
      text: "I've tried many perfumes from luxury brands, but Alherz offers fragrances that are truly unique and long-lasting. Their customer service is exceptional!",
      author: "Sara Ahmed",
      title: "Loyal Customer",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=250&q=80"
    },
    {
      id: 2,
      text: "The gift wrapping service is immaculate. I ordered a perfume set for my wife's birthday, and she was blown away by both the presentation and the fragrances.",
      author: "Mohammed Al-Harbi",
      title: "Satisfied Client",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=250&q=80"
    },
    {
      id: 3,
      text: "Alherz Perfume offers authenticity that's hard to find elsewhere. Their collection of traditional Arabian fragrances is simply unmatched in quality.",
      author: "Layla Nasser",
      title: "Fragrance Enthusiast",
      image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-1.2.1&auto=format&fit=crop&w=250&q=80"
    }
  ];
  
  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const scrollProducts = (direction: 'left' | 'right') => {
    if (productCarouselRef.current) {
      const scrollAmount = 310; // card width + margin
      productCarouselRef.current.scrollLeft += direction === 'right' ? scrollAmount : -scrollAmount;
    }
  };
  
  const nextTestimonial = () => {
    setActiveTestimonial(prev => (prev + 1) % testimonials.length);
  };
  
  const prevTestimonial = () => {
    setActiveTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  
  // Animation effect for elements as they come into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );
    
    document.querySelectorAll('.animate-on-scroll').forEach(el => {
      observer.observe(el);
    });
    
    // Auto advance testimonials
    const testimonialInterval = setInterval(() => {
      nextTestimonial();
    }, 5000);
    
    return () => {
      observer.disconnect();
      clearInterval(testimonialInterval);
    };
  }, []);
  
  return (
    <>
      <HeroSection>
        <BackgroundDecoration />
        <GlassPanel />
        <LightBeam />
        <HeroDecoration />
        <FloatingObject />
        <FloatingObject />
        <StarIcon><FontAwesomeIcon icon={faStar} /></StarIcon>
        <StarIcon><FontAwesomeIcon icon={faStar} /></StarIcon>
        <StarIcon><FontAwesomeIcon icon={faStar} /></StarIcon>
        <StarIcon><FontAwesomeIcon icon={faStar} /></StarIcon>
        <StarIcon><FontAwesomeIcon icon={faStar} /></StarIcon>
          <HeroContent>
            <HeroTitle>
            Experience Luxury at
            <span>Alherz Perfume</span>
            </HeroTitle>
            <HeroSubtitle>
            Discover our curated collection of exquisite fragrances and luxury gifts 
            crafted for those who appreciate refined elegance and timeless sophistication.
            </HeroSubtitle>
            <HeroButtons>
            <PrimaryButton as={Link} to="/products">Explore Collection</PrimaryButton>
            <SecondaryButton as={Link} to="/collections">View Bestsellers</SecondaryButton>
            </HeroButtons>
          </HeroContent>
        <ScrollIndicator onClick={scrollToFeatures}>
          <FontAwesomeIcon icon={faChevronDown} />
        </ScrollIndicator>
        <HeroOverlay />
      </HeroSection>
      
      <BannerSection>
        <Container>
          <BannerTitle>
            <span>Free Shipping on Orders Over $100</span>
            <BannerDivider />
            <span>Same Day Delivery in Manama</span>
            <BannerDivider />
            <span>Luxury Gift Wrapping</span>
          </BannerTitle>
        </Container>
      </BannerSection>
      
      <ShowcaseSection>
        <Container>
          <FadeInElement>
            <SectionTitle>Discover Our Signature Collection</SectionTitle>
          </FadeInElement>
          
          <ShowcaseGrid>
            <ShowcaseImage>
              <img src={showcaseImage} alt="Signature Collection" />
            </ShowcaseImage>
            <ShowcaseContent>
              <FadeInElement delay="0.2s">
                <ShowcaseTitle>Arabian Luxury</ShowcaseTitle>
                <ShowcaseDescription>
                  Our signature collection embodies the rich heritage of Arabian perfumery, 
                  blending traditional oud with modern notes for a truly extraordinary fragrance experience.
                </ShowcaseDescription>
                
                <FeatureListItem>
                  <FontAwesomeIcon icon={faVial} />
                  <p>Crafted with rare and premium ingredients</p>
                </FeatureListItem>
                <FeatureListItem>
                  <FontAwesomeIcon icon={faLeaf} />
                  <p>Long-lasting fragrance composition</p>
                </FeatureListItem>
                <FeatureListItem>
                  <FontAwesomeIcon icon={faPalette} />
                  <p>Elegant bottle design with 24k gold accents</p>
                </FeatureListItem>
                
                <Button 
                  variant="primary" 
                  size="large" 
                  as={Link} 
                  to="/products/signature-collection"
                  style={{ marginTop: theme.spacing.lg }}
                >
                  View Collection
                </Button>
              </FadeInElement>
            </ShowcaseContent>
          </ShowcaseGrid>
        </Container>
      </ShowcaseSection>
      
      <TrendingSection>
        <Container>
          <div className="animate-on-scroll">
            <SectionTitle>Trending Now</SectionTitle>
            <SectionSubtitle>Discover our most popular and sought-after fragrances</SectionSubtitle>
          </div>
          
          <CarouselNavigation>
            <NavButton onClick={() => scrollProducts('left')}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </NavButton>
            <NavButton onClick={() => scrollProducts('right')}>
              <FontAwesomeIcon icon={faChevronRight} />
            </NavButton>
          </CarouselNavigation>
          
          <ProductCarousel ref={productCarouselRef}>
            {trendingProducts.map((product) => (
              <ProductCarouselItem key={product.id}>
                <ProductCard product={product} />
              </ProductCarouselItem>
            ))}
          </ProductCarousel>
        </Container>
      </TrendingSection>
      
      <ProductSection>
        <Container>
          <div className="animate-on-scroll">
            <SectionTitle>Featured Products</SectionTitle>
            <SectionSubtitle>
              Discover our collection of best-selling perfumes and luxury gifts
            </SectionSubtitle>
          </div>
          
          <Grid 
            columns={4} 
            tabletColumns={2} 
            mobileColumns={1}
            gap={theme.spacing.lg}
          >
            {featuredProducts.map((product, index) => (
              <div key={product.id} className="animate-on-scroll" style={{ animationDelay: `${index * 0.1}s` }}>
                {product.discountPrice && (
                  <FeaturedBadge type="primary" size="small">
                    SALE
                  </FeaturedBadge>
                )}
                <ProductCard product={product} />
              </div>
            ))}
          </Grid>
          
          <div style={{ textAlign: 'center', marginTop: theme.spacing.xl }}>
            <ViewAllButton as={Link} to="/products" variant="outline" size="large">
              View All Products <FontAwesomeIcon icon={faArrowRight} />
            </ViewAllButton>
          </div>
        </Container>
      </ProductSection>
      
      <TestimonialSection>
        <Container>
          <div className="animate-on-scroll">
            <SectionTitle>What Our Customers Say</SectionTitle>
          </div>
          
          <TestimonialCarousel>
            {testimonials.map((testimonial, index) => (
              <TestimonialItem key={testimonial.id} active={index === activeTestimonial}>
                <QuoteIcon>
                  <FontAwesomeIcon icon={faQuoteLeft} />
                </QuoteIcon>
                <TestimonialText>
                  {testimonial.text}
                </TestimonialText>
                <TestimonialAuthor>
                  <AuthorImage src={testimonial.image} alt={testimonial.author} />
                  <AuthorInfo>
                    <AuthorName>{testimonial.author}</AuthorName>
                    <AuthorTitle>{testimonial.title}</AuthorTitle>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialItem>
            ))}
            
            <CarouselControls>
              <CarouselButton onClick={prevTestimonial}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </CarouselButton>
              <CarouselButton onClick={nextTestimonial}>
                <FontAwesomeIcon icon={faChevronRight} />
              </CarouselButton>
            </CarouselControls>
            
            <CarouselIndicators>
              {testimonials.map((_, index) => (
                <CarouselIndicator 
                  key={index}
                  active={index === activeTestimonial}
                  onClick={() => setActiveTestimonial(index)}
                />
              ))}
            </CarouselIndicators>
          </TestimonialCarousel>
        </Container>
      </TestimonialSection>
      
      <FeatureSection ref={featuresRef}>
        <Container>
          <div className="animate-on-scroll">
            <SectionTitle>The Alherz Experience</SectionTitle>
            <SectionSubtitle>
              We pride ourselves on providing an exceptional experience from browsing to delivery
            </SectionSubtitle>
          </div>
          <FeatureGrid>
            <FeatureItem className="animate-on-scroll" style={{ animationDelay: '0.1s' }}>
              <FontAwesomeIcon icon={faShippingFast} />
              <h3>Premium Delivery</h3>
              <p>Same day delivery in Manama and next day delivery to other cities in Bahrain</p>
            </FeatureItem>
            <FeatureItem className="animate-on-scroll" style={{ animationDelay: '0.2s' }}>
              <FontAwesomeIcon icon={faGift} />
              <h3>Luxury Packaging</h3>
              <p>Complimentary gift wrapping with handcrafted materials and personalized message cards</p>
            </FeatureItem>
            <FeatureItem className="animate-on-scroll" style={{ animationDelay: '0.3s' }}>
              <FontAwesomeIcon icon={faLeaf} />
              <h3>Authentic Products</h3>
              <p>Genuine luxury fragrances sourced directly from top perfumeries with authenticity certificates</p>
            </FeatureItem>
            <FeatureItem className="animate-on-scroll" style={{ animationDelay: '0.4s' }}>
              <FontAwesomeIcon icon={faPercent} />
              <h3>Rewards Program</h3>
              <p>Exclusive rewards for our loyal customers including early access to new collections and VIP events</p>
            </FeatureItem>
          </FeatureGrid>
        </Container>
      </FeatureSection>
      
      <Section>
        <Container>
          <div className="animate-on-scroll">
            <SectionTitle>Browse By Category</SectionTitle>
            <SectionSubtitle>Explore our curated collections for every preference and occasion</SectionSubtitle>
          </div>
          
          <Grid 
            columns={3} 
            tabletColumns={2}
            mobileColumns={1}
            gap={theme.spacing.xl}
          >
            {mockCategories.map((category, index) => (
              <div key={category.id} className="animate-on-scroll" style={{ animationDelay: `${index * 0.1}s` }}>
                <CategoryCard to={`/category/${category.id}`}>
                  <img src={category.image} alt={category.name} />
                  <CategoryOverlay>
                    <CategoryName>{category.name}</CategoryName>
                    <CategoryDescription>{category.description}</CategoryDescription>
                    <CategoryButton>
                      Shop Now <FontAwesomeIcon icon={faArrowRight} />
                    </CategoryButton>
                  </CategoryOverlay>
                </CategoryCard>
              </div>
            ))}
          </Grid>
        </Container>
      </Section>
    </>
  );
};

export default HomePage; 